import { buildFrameConfigFromChosenOptions } from '@/utils/frameConfigFromOptions.js'
import {
  artworkFitsMatWidthConstraints,
  constraintFitsDimensions,
  getMatOptionNamesExceedingSize,
  resolveResizeConstraintsFromChosenOptions,
} from '@/utils/sizingPolicy.js'

const MAT_MATERIAL_ORDER = ['White core', 'Premium white core', 'Rag', '8 ply']

const MAT_GROUP_ORDER = [
  'Whites & off whites',
  'Grays & black',
  'Golds & silver',
  'Reds & oranges',
  'Yellow & mustards',
  'Browns & tans',
  'Greens',
  'Blues',
  'Purples & roses',
]

function colorLightness(colorStr) {
  const m = String(colorStr || '').match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/)
  if (!m) return 0.5
  const r = parseInt(m[1]) / 255, g = parseInt(m[2]) / 255, b = parseInt(m[3]) / 255
  return (Math.max(r, g, b) + Math.min(r, g, b)) / 2
}

export function getOptionLabel(opt) {
  const val = opt?.value
  if (val === null || val === undefined || val === '') return opt?.name || ''
  if (typeof val === 'number') return String(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  return String(val)
}

export function isMatColorCategory(category) {
  return /^mat\s*\d+$/i.test(String(category || '').trim())
}

export function isMatWidthCategory(category) {
  return /mat\s*width/i.test(String(category || ''))
}

/**
 * The hang-hardware category — the one option group whose subject is on the
 * back of the piece rather than the front.
 */
export function isHangOptionCategory(category) {
  return /^hang\b/i.test(String(category || '').trim())
}

/** Count how many Mat N color categories have a selection in an option-state map. */
export function countSelectedMatColors(optionState = {}) {
  let count = 0
  for (const n of [1, 2, 3]) {
    const re = new RegExp(`^Mat\\s*${n}$`, 'i')
    for (const [category, optionId] of Object.entries(optionState || {})) {
      if (optionId && re.test(category)) { count++; break }
    }
  }
  return count
}

/** Whether a higher-numbered mat slot still has a color selection. */
export function hasMatColorSelectionAbove(matNum, optionState = {}) {
  if (matNum == null) return false
  for (let n = matNum + 1; n <= 3; n++) {
    const re = new RegExp(`^Mat\\s*${n}$`, 'i')
    for (const [category, optionId] of Object.entries(optionState || {})) {
      if (optionId && re.test(category)) return true
    }
  }
  return false
}

/** Keep the Number of mats option aligned with how many mat colors are selected. */
export function syncNumberOfMatsSelection(optionState = {}, groupedOptions = {}, count = countSelectedMatColors(optionState)) {
  const next = { ...(optionState || {}) }
  for (const [category, options] of Object.entries(groupedOptions || {})) {
    if (!/number\s*of\s*mats/i.test(category)) continue
    const targetOpt = (options || []).find(o => {
      const v = parseInt(o.value ?? o.num_value ?? o.char_value, 10)
      return v === count
    })
    if (targetOpt && next[category] !== targetOpt.id) {
      next[category] = targetOpt.id
    }
    break
  }
  return next
}

/** Keep chosen mat support options consistent with the selected mat layers. */
export function normalizeChosenMatSupportOptions(chosenOptions = [], availableOptions = [], matCount = 0) {
  const normalizedCount = Math.max(0, Number(matCount) || 0)
  const countOption = availableOptions.find((option) => {
    if (!/number\s*of\s*mats/i.test(String(option?.name || ''))) return false
    return parseInt(option.value ?? option.num_value ?? option.char_value, 10) === normalizedCount
  })
  const normalizedCountOption = countOption
    ? {
        name: countOption.name,
        value: countOption.value,
        option_id: countOption.id,
        extra_data: countOption.extra_data || {},
      }
    : null
  const next = []
  let countOptionAdded = false

  chosenOptions.forEach((option) => {
    const name = String(option?.name || '')
    if (/number\s*of\s*mats/i.test(name)) {
      if (normalizedCountOption) next.push(normalizedCountOption)
      countOptionAdded = true
      return
    }
    if (normalizedCount === 0 && isMatWidthCategory(name)) return
    next.push(option)
  })

  if (!countOptionAdded && normalizedCountOption) next.push(normalizedCountOption)
  return next
}

/**
 * Remove mat layers whose sheets cannot contain the artwork, then keep the
 * dependent mat-count and width selections consistent. Used by both resize
 * and substrate changes so every edit path applies the same product rule.
 */
export function normalizeChosenMatsForSize({
  width,
  height,
  substrate,
  chosenOptions = [],
  availableOptions = [],
} = {}) {
  const isMat = option => /^mat\s*[123]$/i.test(String(option?.name || '').trim())
  const mats = chosenOptions.filter(isMat)
  const nonMats = chosenOptions.filter(option => !isMat(option))
  let remainingMats = [...mats]

  while (remainingMats.length) {
    const candidateChosen = [...nonMats, ...remainingMats]
    const config = buildFrameConfigFromChosenOptions(substrate, candidateChosen)
    const constraints = resolveResizeConstraintsFromChosenOptions(candidateChosen, {
      matWidthsArray: config?.matWidthsArray || [],
    })
    // A frame's maximum applies to the finished piece. If its profile fits the
    // artwork alone but not the selected mat stack, discard the mats and let
    // the existing frame constraint report only a genuinely oversized piece.
    if (constraints.some(constraint => (
      constraint.source === 'frame' && !constraintFitsDimensions(width, height, constraint)
    ))) {
      remainingMats = []
      break
    }
    const exceededNames = new Set(getMatOptionNamesExceedingSize(
      width,
      height,
      candidateChosen,
      config?.matWidthsArray || [],
    ))
    if (!exceededNames.size) break
    const next = remainingMats.filter(mat => !exceededNames.has(mat.name))
    if (next.length === remainingMats.length) break
    remainingMats = next
  }

  const remainingKeys = new Set(remainingMats.map(mat => `${mat.name}:${mat.option_id}`))
  const validChosen = chosenOptions.filter(option => (
    !isMat(option) || remainingKeys.has(`${option.name}:${option.option_id}`)
  ))
  return normalizeChosenMatSupportOptions(validChosen, availableOptions, remainingMats.length)
}

export function getMatOptionsGrouped(options = []) {
  const groups = {}
  for (const opt of options) {
    const group = opt.extra_data?.color_group || 'Other'
    const material = opt.extra_data?.material || ''
    if (!groups[group]) groups[group] = {}
    if (!groups[group][material]) groups[group][material] = []
    groups[group][material].push({
      value: opt.id,
      text: getOptionLabel(opt),
      color: opt.extra_data?.color || null,
      material,
      thumbnailImage: null,
    })
  }

  // Decorate-sort-undecorate: as a bare comparator this re-parsed both colour
  // strings on every comparison, so an n-option group cost O(n log n) parses
  // instead of n.
  const sortedByLightnessDesc = (list) => list
    .map(option => ({ option, lightness: colorLightness(option.color) }))
    .sort((a, b) => b.lightness - a.lightness)
    .map(entry => entry.option)
  return Object.keys(groups)
    .sort((a, b) => {
      const ai = MAT_GROUP_ORDER.indexOf(a)
      const bi = MAT_GROUP_ORDER.indexOf(b)
      return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi)
    })
    .map(group => {
      const materialMap = groups[group]
      const materials = Object.keys(materialMap).sort((a, b) => {
        const ai = MAT_MATERIAL_ORDER.indexOf(a)
        const bi = MAT_MATERIAL_ORDER.indexOf(b)
        return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi)
      })
      const useSubheadings = materials.length > 1
      const colors = []
      for (const material of materials) {
        if (useSubheadings) {
          colors.push({
            value: '__mat_label__',
            text: material,
            color: null,
            thumbnailImage: null,
            isMaterialLabel: true,
            materialLabel: material,
            $isDisabled: true,
          })
        }
        colors.push(...sortedByLightnessDesc(materialMap[material]))
      }
      return { group, colors }
    })
}

/** Mark mat color options that fail an availability check as disabled (keeps current selection enabled). */
export function applyMatOptionAvailability(groupedOptions = [], { selectedOptionId = null, isOptionAvailable } = {}) {
  if (typeof isOptionAvailable !== 'function') return groupedOptions

  return groupedOptions.map(group => ({
    ...group,
    colors: group.colors.map(opt => {
      if (opt.isMaterialLabel || opt.value === '__mat_label__') return opt
      if (isOptionAvailable(opt.value)) return opt
      if (selectedOptionId != null && String(opt.value) === String(selectedOptionId)) return opt
      return { ...opt, $isDisabled: true }
    }),
  }))
}

export function toChosenOptionEntry(category, opt) {
  if (!opt) return null
  return {
    name: category,
    value: opt.value ?? null,
    option_id: opt.id,
    extra_data: opt.extra_data || null,
  }
}

/**
 * Returns mat option ids that cannot fit the current artwork size.
 * Other mat slots are checked once; only the open slot is evaluated per candidate.
 */
export function computeUnavailableMatOptionIds({
  width,
  height,
  category,
  rawOptions = [],
  selectedOptions = {},
  optionById = new Map(),
  matCategoryForNumber,
  matWidthsForLayerCount,
}) {
  const parsedWidth = Number(width)
  const parsedHeight = Number(height)
  if (!Number.isFinite(parsedWidth) || !Number.isFinite(parsedHeight)) return new Set()

  const unavailable = new Set()
  const baseState = { ...(selectedOptions || {}) }
  const resolveCategory = typeof matCategoryForNumber === 'function' ? matCategoryForNumber : () => null
  const matWidthsFor = typeof matWidthsForLayerCount === 'function' ? matWidthsForLayerCount : () => []

  const fixedMatEntries = []
  for (const n of [1, 2, 3]) {
    const cat = resolveCategory(n)
    if (!cat || cat === category) continue
    const id = baseState[cat]
    if (!id) continue
    const opt = optionById.get(id) ?? optionById.get(String(id))
    const entry = toChosenOptionEntry(cat, opt)
    if (entry) fixedMatEntries.push(entry)
  }

  const fixedPassByNumMats = {}

  for (const opt of rawOptions) {
    const optionId = opt?.id
    if (optionId == null) continue

    const nextState = { ...baseState, [category]: optionId }
    const numMats = countSelectedMatColors(nextState)
    if (!numMats) continue

    const matWidthsArray = matWidthsFor(numMats)

    if (fixedPassByNumMats[numMats] === undefined) {
      fixedPassByNumMats[numMats] = artworkFitsMatWidthConstraints(
        parsedWidth,
        parsedHeight,
        fixedMatEntries,
        matWidthsArray,
      )
    }
    if (!fixedPassByNumMats[numMats]) {
      unavailable.add(optionId)
      continue
    }

    const candidateEntry = toChosenOptionEntry(category, opt)
    if (!candidateEntry) {
      unavailable.add(optionId)
      continue
    }

    const candidateFits = artworkFitsMatWidthConstraints(
      parsedWidth,
      parsedHeight,
      [candidateEntry],
      matWidthsArray,
    )
    if (!candidateFits) unavailable.add(optionId)
  }

  return unavailable
}

/** Apply a precomputed unavailable-id set to grouped mat multiselect options. */
export function markMatOptionsUnavailable(groupedOptions = [], unavailableIds = new Set(), selectedOptionId = null) {
  if (!unavailableIds?.size) return groupedOptions

  return groupedOptions.map(group => ({
    ...group,
    colors: group.colors.map(opt => {
      if (opt.isMaterialLabel || opt.value === '__mat_label__') return opt
      if (!unavailableIds.has(opt.value)) return opt
      if (selectedOptionId != null && String(opt.value) === String(selectedOptionId)) return opt
      return { ...opt, $isDisabled: true }
    }),
  }))
}

export function getSelectedMatOption(category, rawOptions = [], selectedOptions = {}) {
  const id = selectedOptions[category]
  if (!id) return null
  const opt = rawOptions.find(o => String(o.id) === String(id))
  if (!opt) return null
  return {
    value: opt.id,
    text: getOptionLabel(opt),
    color: opt.extra_data?.color || null,
    material: opt.extra_data?.material || null,
    thumbnailImage: null,
  }
}
